import React from 'react'

import { X } from 'lucide-react'

import { Button } from '~/client/src/shared/components/Button/Button'
import { Checkbox } from '~/client/src/shared/components/shadcn-ui/checkbox'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import { WidgetId, WIDGET_CONFIGS } from '../../Constants/WidgetConfig'

interface WidgetSection {
  title: string
  description: string
  widgets: WidgetId[]
}

interface ISettingsPanelProps {
  widgetVisibility: Record<string, boolean>
  onClose: () => void
  onToggleWidgetVisibility: (widgetId: string) => void
}

const getWidgetSections = (): WidgetSection[] => {
  const sections = Localization.translator.dashboard.sections
  return [
    {
      title: sections.keyMetrics.title,
      description: sections.keyMetrics.description,
      widgets: [
        WidgetId.WORKFLOWS_COUNT,
        WidgetId.COMPANIES_COUNT,
        WidgetId.USERS_COUNT,
        WidgetId.AVERAGE_DURATION,
      ],
    },
    {
      title: sections.planningEfficiency.title,
      description: sections.planningEfficiency.description,
      widgets: [WidgetId.ADVANCED_PLANNING],
    },
    {
      title: sections.workflowStatus.title,
      description: sections.workflowStatus.description,
      widgets: [WidgetId.WORKFLOWS_BY_STATUS, WidgetId.ACTIVE_VS_COMPLETED],
    },
    {
      title: sections.workflowAnalytics.title,
      description: sections.workflowAnalytics.description,
      widgets: [WidgetId.WORKFLOWS_BY_CATEGORY, WidgetId.WORKFLOWS_TIMELINE],
    },
    {
      title: sections.recentActivity.title,
      description: sections.recentActivity.description,
      widgets: [WidgetId.RECENT_WORKFLOWS],
    },
    {
      title: sections.locationAnalytics.title,
      description: sections.locationAnalytics.description,
      widgets: [WidgetId.LOCATIONS_WORKFLOWS, WidgetId.LOCATIONS_BY_TYPE],
    },
    {
      title: sections.equipmentResources.title,
      description: sections.equipmentResources.description,
      widgets: [
        WidgetId.EQUIPMENT_BOOKINGS_WORKFLOWS,
        WidgetId.EQUIPMENT_BOOKINGS_HOURS,
      ],
    },
  ]
}

const SettingsPanel: React.FC<ISettingsPanelProps> = ({
  widgetVisibility,
  onClose,
  onToggleWidgetVisibility,
}) => {
  const sections = getWidgetSections()

  return (
    <div className="settings-panel">
      <div className="settings-panel-header">
        <h3 className="settings-panel-title">
          {Localization.translator.dashboard.settings}
        </h3>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X />
        </Button>
      </div>
      <div className="settings-panel-content">
        <p className="settings-panel-description">
          {Localization.translator.dashboard.settingsDescription}
        </p>
        <div className="widget-sections">
          {sections.map((section, index) => (
            <div key={index} className="widget-section">
              <div className="widget-section-header">
                <h4 className="widget-section-title">{section.title}</h4>
                <span className="widget-section-description">
                  {section.description}
                </span>
              </div>
              <div className="widget-toggles">
                {section.widgets.map(widgetId => {
                  const config = WIDGET_CONFIGS.find(c => c.id === widgetId)
                  if (!config) return null
                  return (
                    <label key={config.id} className="widget-toggle">
                      <Checkbox
                        checked={widgetVisibility[config.id]}
                        onCheckedChange={() =>
                          onToggleWidgetVisibility(config.id)
                        }
                      />
                      <span className="widget-toggle-label">
                        {config.title}
                      </span>
                    </label>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default SettingsPanel
